import { Controller } from "@hotwired/stimulus"
import { autoUpdate, computePosition, flip, hide, shift } from "@floating-ui/dom"
import type { Placement } from "@floating-ui/dom"

export default class extends Controller<HTMLElement> {
  static targets = ["menu", "trigger"]
  static values = {
    placement: { type: String, default: "bottom-start" },
    trigger: { type: String, default: "click" },
  }

  declare readonly menuTarget: HTMLElement
  declare readonly triggerTarget: HTMLElement
  declare readonly placementValue: string
  declare readonly triggerValue: string
  declare listeners?: AbortController
  declare stopAutoUpdate?: () => void

  connect() {
    const { signal } = (this.listeners = new AbortController())
    this.menuTarget.addEventListener("toggle", (event) => this.toggled(event as ToggleEvent), {
      signal,
    })

    if (this.triggerValue === "hover") {
      this.element.addEventListener("pointerenter", () => this.opensOnHover() && this.open(), {
        signal,
      })
      this.element.addEventListener("pointerleave", () => this.opensOnHover() && this.close(), {
        signal,
      })
      this.element.addEventListener("focusin", () => this.opensOnHover() && this.open(), {
        signal,
      })
      this.element.addEventListener("focusout", () => this.opensOnHover() && this.close(), {
        signal,
      })
    }

    // Light dismiss closes the menu on pointerdown, before click fires;
    // only toggle when the tap didn't start with the menu open, or the
    // same tap that closed it reopens it.
    let openAtPointerdown = false
    this.triggerTarget.addEventListener(
      "pointerdown",
      () => {
        openAtPointerdown = this.menuTarget.matches(":popover-open")
      },
      { signal },
    )
    this.triggerTarget.addEventListener(
      "click",
      () => {
        if (!this.opensOnHover() && !openAtPointerdown) this.toggle()
        openAtPointerdown = false
      },
      { signal },
    )
  }

  // Checked on every event, not once on connect: the same page can switch between
  // mouse and touch (a touchscreen laptop, DevTools device mode).
  opensOnHover() {
    return this.triggerValue === "hover" && matchMedia("(hover: hover)").matches
  }

  disconnect() {
    this.listeners?.abort()
    this.close()
    this.stopAutoUpdate?.()
  }

  open() {
    if (!this.menuTarget.matches(":popover-open")) this.menuTarget.showPopover()
  }

  toggle() {
    this.menuTarget.togglePopover()
  }

  close() {
    if (this.menuTarget.matches(":popover-open")) this.menuTarget.hidePopover()
  }

  // The popover also closes itself (Escape, light dismiss), so track its real state.
  toggled(event: ToggleEvent) {
    this.triggerTarget.setAttribute("aria-expanded", String(event.newState === "open"))
    this.stopAutoUpdate?.()
    this.stopAutoUpdate = undefined

    if (event.newState === "open") {
      this.stopAutoUpdate = autoUpdate(this.element, this.menuTarget, () => this.reposition())
    }
  }

  async reposition() {
    const { x, y, middlewareData } = await computePosition(this.element, this.menuTarget, {
      strategy: "fixed",
      placement: this.placementValue as Placement,
      middleware: [flip({ padding: 16 }), shift({ padding: 16 }), hide()],
    })

    // Anchor scrolled fully out of view — close instead of hiding, so the
    // menu can't linger open and resurface when the anchor scrolls back.
    if (middlewareData.hide?.referenceHidden) return this.close()

    Object.assign(this.menuTarget.style, { left: `${x}px`, top: `${y}px` })
  }
}
